/**
 * lightning.ts — lightning you can SEE (fire realism R7, 2026-09-25).
 *
 * Until now a storm's lightning was an ambient flash: no bolt was ever drawn and no light moved inside the cloud
 * deck. The engine's `LightningEffect` builds a TubeGeometry and a PointLight PER BOLT — a new light breaks the
 * light-set identity law (every lit pipeline rebuilds) and a geometry per bolt churns the GPU. This is the Atlas
 * version:
 *
 *   · THE BOLT (pure, seeded): midpoint displacement of the cloud-to-ground channel, with BRANCHES spawned from
 *     displaced midpoints (dimmer, thinner, shorter each generation) — written into a PREALLOCATED ribbon buffer
 *     (`BOLT_MAX_SEGMENTS`, one interleaved buffer = one vertex buffer), never a new geometry.
 *   · THE RIBBON (TSL): every segment is a camera-facing strip expanded in VIEW space across its own screen
 *     direction, never narrower than ~2.5 px at any distance (a channel kilometres away is still a line); a white
 *     HDR core (≈ 30) with a blue glow falloff across the strip; pure emission (premultiplied, α = 0).
 *   · THE TIMING: a dim stepped leader, then 3–4 RETURN STROKES inside ~200 ms (`strokeEnvelope`), then the bloom
 *     carries the afterglow; the envelope drives the bolt, the parked directional flash and the cloud lobe.
 *   · MOST STROKES STAY IN THE CLOUD (`strikeKind`): an intra-cloud flash lights the deck from inside (a lobe
 *     uniform in the cloud shaders) and the directional flash, with no bolt drawn.
 * `verify:fire` pins the bolt's geometry (endpoints, connectivity, branch budget, widths) and the envelope.
 */

export interface V3 { x: number; y: number; z: number }
export const BOLT_MAX_SEGMENTS = 480;
/** One segment: endpoints, width (m), relative intensity, generation. */
export interface BoltSeg { a: V3; b: V3; w: number; i: number; gen: number; branch: boolean }

function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const sub = (a: V3, b: V3): V3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: V3, b: V3): V3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const mul = (a: V3, k: number): V3 => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const len = (a: V3): number => Math.hypot(a.x, a.y, a.z);
function perpPair(d: V3): [V3, V3] {
  const n = mul(d, 1 / Math.max(1e-9, len(d)));
  const ref = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const u = { x: n.y * ref.z - n.z * ref.y, y: n.z * ref.x - n.x * ref.z, z: n.x * ref.y - n.y * ref.x };
  const ul = len(u) || 1;
  const uu = mul(u, 1 / ul);
  const v = { x: n.y * uu.z - n.z * uu.y, y: n.z * uu.x - n.x * uu.z, z: n.x * uu.y - n.y * uu.x };
  return [uu, v];
}

/**
 * A cloud-to-ground bolt from `top` (the cloud base) to `ground`: `generations` rounds of midpoint displacement
 * (each displaces every segment's midpoint off its line by up to `jitter` × its length) and branching (a branch
 * leaves a displaced midpoint, bent 20–45° off the parent, 0.45–0.75 of its remaining length, at `branchDecay` ×
 * the parent's intensity and 0.55 × its width). Capped at BOLT_MAX_SEGMENTS.
 */
export function buildBolt(seed: number, top: V3, ground: V3, opts: { generations?: number; jitter?: number; branchProb?: number; branchDecay?: number; width?: number } = {}): BoltSeg[] {
  const r = rng(seed);
  const gens = opts.generations ?? 7, jitter = opts.jitter ?? 0.22, bp = opts.branchProb ?? 0.32, decay = opts.branchDecay ?? 0.5;
  let segs: BoltSeg[] = [{ a: top, b: ground, w: opts.width ?? 1.4, i: 1, gen: 0, branch: false }];
  for (let g = 1; g <= gens; g++) {
    const next: BoltSeg[] = [];
    for (const s of segs) {
      const d = sub(s.b, s.a);
      const L = len(d);
      const [u, v] = perpPair(d);
      const off = add(mul(u, (r() - 0.5) * 2 * jitter * L), mul(v, (r() - 0.5) * 2 * jitter * L));
      const mid = add(mul(add(s.a, s.b), 0.5), off);
      next.push({ a: s.a, b: mid, w: s.w, i: s.i, gen: g, branch: s.branch }, { a: mid, b: s.b, w: s.w, i: s.i, gen: g, branch: s.branch });
      // Branches: likelier from the main channel and early generations; downward-leaning (they chase the ground).
      if (next.length < BOLT_MAX_SEGMENTS - 4 && r() < bp * (s.branch ? 0.45 : 1) * (1 - g / (gens + 2))) {
        const dir = sub(s.b, mid);
        const dl = len(dir);
        const ang = (20 + r() * 25) * (Math.PI / 180) * (r() < 0.5 ? -1 : 1);
        const [pu] = perpPair(dir);
        const bent = add(mul(dir, Math.cos(ang)), mul(pu, Math.sin(ang) * dl));
        const k = 0.45 + r() * 0.3;
        next.push({ a: mid, b: add(mid, mul(bent, k)), w: s.w * 0.55, i: s.i * decay, gen: g, branch: true });
      }
    }
    segs = next.slice(0, BOLT_MAX_SEGMENTS);
  }
  return segs;
}

/** The kind of a strike from its hash: most stay inside the cloud (IC), about a third reach the ground (CG). */
export const CLOUD_TO_GROUND_SHARE = 0.35;
export function strikeKind(hash01: number): 'cg' | 'ic' {
  return ((hash01 * 7.137) % 1) < CLOUD_TO_GROUND_SHARE ? 'cg' : 'ic';
}

/**
 * The flash's brightness at `tS` seconds after it began (0..1): a dim stepped leader (0–25 ms), then 3–4 return
 * strokes inside ~200 ms (seeded offsets), each a fast rise and a ~30 ms decay; nothing past ~0.3 s (the bloom and
 * the sky's afterglow carry the rest).
 */
export function strokeTimes(seed: number): number[] {
  const r = rng(seed ^ 0xb017);
  const n = 3 + (r() < 0.5 ? 1 : 0);
  const t: number[] = [0.025];
  for (let i = 1; i < n; i++) t.push(t[i - 1] + 0.035 + r() * 0.045);
  return t;
}
export function strokeEnvelope(tS: number, times: readonly number[]): number {
  if (tS < 0) return 0;
  let v = tS < 0.025 ? 0.12 * (tS / 0.025) : 0;
  for (let i = 0; i < times.length; i++) {
    const d = tS - times[i];
    if (d < 0) continue;
    const peak = i === 0 ? 1 : 0.8 - 0.12 * i;
    v = Math.max(v, peak * (d < 0.003 ? d / 0.003 : Math.exp(-(d - 0.003) / 0.03)));
  }
  return tS > 0.45 ? 0 : v;
}
/** How long a flash lasts (s) — after this every channel is dark. */
export const FLASH_S = 0.45;

/**
 * The share of the storm's old AMBIENT flash that stays once the directional flash and the drawn bolt exist
 * (the brief: cut to ~30 %). WeatherModule multiplies its flash target by this; 1 until the lightning module
 * has armed (a world without it keeps the old flash).
 */
let ambientShare = 1;
export function lightningAmbientShare(): number { return ambientShare; }
export function setLightningAmbientShare(v: number): void { ambientShare = Math.min(1, Math.max(0, v)); }
